import * as pc from 'playcanvas';
import { createGlowMaterial } from '../entities/materials.js';
import { LEVEL_CONFIG } from '../config/levels.js';
import { screenShake } from './camera.js';
import { createHitParticles } from './combat.js';

// Laser state
let laserBeam = null;
let laserChargeEffect = null;
let laserTarget = null;

// Create laser charge effect
export function createLaserChargeEffect(boss, level) {
    const config = LEVEL_CONFIG[level];

    laserChargeEffect = new pc.Entity('LaserCharge');
    laserChargeEffect.addComponent('render', {
        type: 'sphere',
        material: createGlowMaterial(config.bossGlowColor, 2.0)
    });
    laserChargeEffect.setLocalScale(0.1, 0.1, 0.1);
    boss.addChild(laserChargeEffect);
    laserChargeEffect.setLocalPosition(0, 0, 0.6);
}

// Update laser charge effect
export function updateLaserChargeEffect(intensity) {
    if (!laserChargeEffect) return;

    const scale = 0.1 + intensity * 0.5;
    laserChargeEffect.setLocalScale(scale, scale, scale);
}

// Fire the laser beam
export function fireLaserBeam(boss, target, level) {
    // Remove charge effect
    if (laserChargeEffect) {
        laserChargeEffect.destroy();
        laserChargeEffect = null;
    }

    const config = LEVEL_CONFIG[level];
    const bossPos = boss.getPosition();

    // Store target for collision checking
    laserTarget = target.clone();

    // Calculate direction to target
    const dx = laserTarget.x - bossPos.x;
    const dz = laserTarget.z - bossPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    // Get app reference
    const app = boss._app || window.app;

    // Create laser beam
    laserBeam = new pc.Entity('LaserBeam');
    laserBeam.addComponent('render', {
        type: 'cylinder',
        material: createGlowMaterial(config.bossGlowColor, 2.5)
    });

    // Position and rotate beam
    const beamLength = 20;
    const midX = bossPos.x + (dx / dist) * beamLength / 2;
    const midZ = bossPos.z + (dz / dist) * beamLength / 2;

    laserBeam.setPosition(midX, bossPos.y, midZ);
    laserBeam.setLocalScale(0.4, beamLength, 0.4);

    // Rotate to point at target
    const angle = Math.atan2(dx, dz) * (180 / Math.PI);
    laserBeam.setEulerAngles(90, angle, 0);

    app.root.addChild(laserBeam);

    // Store beam direction for collision
    laserBeam.dirX = dx / dist;
    laserBeam.dirZ = dz / dist;
    laserBeam.startX = bossPos.x;
    laserBeam.startZ = bossPos.z;
    laserBeam.bossY = bossPos.y;

    // Screen shake
    screenShake(0.4);

    // Create impact particles at end
    const endX = bossPos.x + (dx / dist) * beamLength;
    const endZ = bossPos.z + (dz / dist) * beamLength;
    createHitParticles(app, new pc.Vec3(endX, bossPos.y, endZ));
}

// Update laser beam visual
export function updateLaserBeam() {
    if (!laserBeam) return;

    // Pulse effect
    const pulse = 0.4 + Math.sin(Date.now() * 0.02) * 0.1;
    laserBeam.setLocalScale(pulse, 20, pulse);
}

// Check if player is hit by laser
export function checkLaserCollision(player) {
    if (!laserBeam) return false;

    const playerPos = player.getPosition();

    // Calculate distance from player to laser line
    const px = playerPos.x - laserBeam.startX;
    const pz = playerPos.z - laserBeam.startZ;

    // Project player position onto laser direction
    const dot = px * laserBeam.dirX + pz * laserBeam.dirZ;

    // Only check if player is in front of boss
    if (dot < 0) return false;

    // Closest point on laser to player
    const closestX = laserBeam.startX + laserBeam.dirX * dot;
    const closestZ = laserBeam.startZ + laserBeam.dirZ * dot;

    // Distance from player to closest point
    const distX = playerPos.x - closestX;
    const distZ = playerPos.z - closestZ;
    const dist = Math.sqrt(distX * distX + distZ * distZ);

    // Laser has width of about 0.5, player radius about 0.5
    return dist < 1.0;
}

// Destroy the laser beam
export function destroyLaserBeam() {
    if (laserBeam) {
        laserBeam.destroy();
        laserBeam = null;
    }
    if (laserChargeEffect) {
        laserChargeEffect.destroy();
        laserChargeEffect = null;
    }
    laserTarget = null;
}

// Get laser target
export function getLaserTarget() {
    return laserTarget;
}

// Set laser target
export function setLaserTarget(target) {
    laserTarget = target;
}
